/**
 * Dastarkhan API Client
 *
 * Typed API functions for all /dastarkhan/ endpoints.
 * Uses useAuthenticatedFetch from client.ts for authenticated requests.
 */

import { useCallback, useRef, useMemo } from 'react';
import { useAuthenticatedFetch } from './client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
}

export interface DastarkhanUser {
  id: string;
  email: string;
  display_name: string;
  role: 'admin' | 'member';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DastarkhanProject {
  id: string;
  name: string;
  matter_code: string | null;
  description: string | null;
  owner_id: string;
  owner_email: string | null;
  status: 'active' | 'archived';
  has_credentials: boolean;
  neo4j_connected: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProjectMembership {
  id: string;
  user_id: string;
  user_email: string | null;
  user_display_name: string | null;
  project_id: string;
  can_read: boolean;
  can_create: boolean;
  can_update: boolean;
  can_delete: boolean;
  granted_by: string;
  created_at: string;
}

export interface GraphNode {
  element_id: string;
  labels: string[];
  properties: Record<string, unknown>;
}

export interface GraphEdge {
  element_id: string;
  type: string;
  source_element_id: string;
  target_element_id: string;
  properties: Record<string, unknown>;
}

export interface GraphData {
  nodes: GraphNode[];
  edges: GraphEdge[];
  total_nodes: number;
  total_edges: number;
  truncated: boolean;
}

export interface GraphSchema {
  node_labels: string[];
  relationship_types: string[];
  node_property_keys: Record<string, string[]>;
  edge_property_keys: Record<string, string[]>;
}

export interface ChatResponse {
  answer: string;
  cypher_query: string | null;
  results: GraphData | null;
  tabular_data: Record<string, unknown>[] | null;
  context_level_used: string;
}

export interface AuditLogEntry {
  id: number;
  project_id: string;
  user_id: string;
  user_email: string | null;
  operation: 'create' | 'update' | 'delete' | 'rollback';
  entity_type: 'node' | 'edge';
  entity_neo4j_id: string;
  before_state: Record<string, unknown> | null;
  after_state: Record<string, unknown> | null;
  created_at: string;
  is_rolled_back: boolean;
  rollback_batch_id: string | null;
  original_transaction_id: number | null;
}

// ─── Rollback Types ────────────────────────────────────────────────────────

export interface UndoableTransaction {
  id: number;
  operation: 'create' | 'update' | 'delete';
  entity_type: 'node' | 'edge';
  entity_neo4j_id: string;
  before_state: Record<string, unknown> | null;
  after_state: Record<string, unknown> | null;
  created_at: string;
}

export interface UndoResponse {
  status: 'success';
  rolled_back_transaction_id: number;
  rollback_entry_id: number;
  entity_neo4j_id: string | null;
}

export interface UndoConflict {
  status: 'conflict';
  transaction_id: number;
  current_state: Record<string, unknown> | null;
  expected_state: Record<string, unknown> | null;
  restore_state: Record<string, unknown> | null;
}

export interface AffectedEntitySummary {
  entity_type: string;
  entity_neo4j_id: string;
  operation_count: number;
}

export interface AffectedUserSummary {
  user_id: string;
  user_email: string | null;
  transaction_count: number;
}

export interface RollbackPreview {
  target_transaction_id: number;
  total_transactions: number;
  operations_summary: Record<string, number>;
  affected_entities: AffectedEntitySummary[];
  affected_users: AffectedUserSummary[];
}

export interface RollbackConflict {
  status: 'conflict';
  transaction_id: number;
  current_state: Record<string, unknown> | null;
  expected_state: Record<string, unknown> | null;
  restore_state: Record<string, unknown> | null;
}

export interface RollbackResult {
  status: 'success' | 'conflict' | 'aborted';
  rollback_batch_id: string;
  total_processed: number;
  total_skipped?: number;
  total_remaining?: number;
  conflict?: RollbackConflict;
  remaining_count?: number;
  processed_so_far?: number;
}

export interface CredentialTestResult {
  status: 'connected' | 'failed';
  message: string;
}

// ─── Bulk Upload Types (amaryllis format — feature 024) ─────────────────────

export interface UploadSummary {
  nodes_created: number;
  nodes_updated: number;
  edges_created: number;
  edges_updated: number;
  sheets_detected: {
    nodes: string;
    edges: string;
  };
}

export interface UploadRowError {
  sheet: string;
  row: number;
  field: string;
  reason: string;
}

export interface UploadStructuralError {
  scope: 'workbook' | 'sheet';
  sheet?: string;
  reason: string;
}

export type UploadError = UploadRowError | UploadStructuralError;

export interface UploadValidationFailure {
  errors: UploadError[];
}

export function isRowError(err: UploadError): err is UploadRowError {
  return (err as UploadRowError).row !== undefined;
}

// ─── Backup Status Types ──────────────────────────────────────────────────────

export interface BackupStatus {
  last_backup_at: string | null;
  last_backup_status: 'success' | 'failed' | null;
}

export interface ClearResult {
  message: string;
  cleared: {
    neo4j_nodes: number;
    neo4j_edges: number;
    red_flags: number;
    audit_entries: number;
    chat_sessions: number;
  };
}

// ─── API Hook ───────────────────────────────────────────────────────────────

/**
 * Hook providing all dastarkhan API operations.
 * Must be used within a component that has auth context.
 */
export function useDastarkhanApi() {
  const { fetchData } = useAuthenticatedFetch<unknown>();

  // Stabilize fetchData ref to prevent infinite re-render loops.
  // useAuthenticatedFetch recreates fetchData on every render (deps: token, auth).
  // Using a ref ensures downstream useCallbacks have stable references.
  const fetchRef = useRef(fetchData);
  fetchRef.current = fetchData;

  const get = useCallback(
    <T>(endpoint: string) => fetchRef.current(endpoint) as Promise<T | undefined>,
    []
  );

  const post = useCallback(
    <T>(endpoint: string, body: unknown) =>
      fetchRef.current(endpoint, {
        method: 'POST',
        body: JSON.stringify(body),
      }) as Promise<T | undefined>,
    []
  );

  const patch = useCallback(
    <T>(endpoint: string, body: unknown) =>
      fetchRef.current(endpoint, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }) as Promise<T | undefined>,
    []
  );

  const put = useCallback(
    <T>(endpoint: string, body: unknown) =>
      fetchRef.current(endpoint, {
        method: 'PUT',
        body: JSON.stringify(body),
      }) as Promise<T | undefined>,
    []
  );

  const del = useCallback(
    (endpoint: string) =>
      fetchRef.current(endpoint, { method: 'DELETE' }),
    []
  );

  // ─── Users ──────────────────────────────────────────────────────────────

  const getCurrentUser = useCallback(
    () => get<DastarkhanUser>('/dastarkhan/users/me'),
    [get]
  );

  const listUsers = useCallback(
    (page = 1, perPage = 50) =>
      get<PaginatedResponse<DastarkhanUser>>(
        `/dastarkhan/users?page=${page}&per_page=${perPage}`
      ),
    [get]
  );

  const updateUserRole = useCallback(
    (userId: string, role: 'admin' | 'member') =>
      patch<DastarkhanUser>(`/dastarkhan/users/${userId}/role`, { role }),
    [patch]
  );

  // ─── Projects ───────────────────────────────────────────────────────────

  const listProjects = useCallback(
    () => get<DastarkhanProject[]>('/dastarkhan/projects'),
    [get]
  );

  const createProject = useCallback(
    (data: { name: string; matter_code?: string; description?: string }) =>
      post<DastarkhanProject>('/dastarkhan/projects', data),
    [post]
  );

  const getProject = useCallback(
    (projectId: string) =>
      get<DastarkhanProject>(`/dastarkhan/projects/${projectId}`),
    [get]
  );

  const updateProject = useCallback(
    (projectId: string, data: { name?: string; matter_code?: string; description?: string; status?: string }) =>
      patch<DastarkhanProject>(`/dastarkhan/projects/${projectId}`, data),
    [patch]
  );

  const updateCredentials = useCallback(
    (projectId: string, creds: { uri: string; username: string; password: string }) =>
      put<CredentialTestResult>(`/dastarkhan/projects/${projectId}/credentials`, creds),
    [put]
  );

  const testCredentials = useCallback(
    (projectId: string) =>
      post<CredentialTestResult>(`/dastarkhan/projects/${projectId}/credentials/test`, {}),
    [post]
  );

  // ─── Memberships ────────────────────────────────────────────────────────

  const listMembers = useCallback(
    (projectId: string) =>
      get<ProjectMembership[]>(`/dastarkhan/projects/${projectId}/members`),
    [get]
  );

  const addMember = useCallback(
    (projectId: string, userId: string) =>
      post<ProjectMembership>(`/dastarkhan/projects/${projectId}/members`, { user_id: userId }),
    [post]
  );

  const updatePermissions = useCallback(
    (projectId: string, userId: string, perms: { can_update?: boolean; can_delete?: boolean }) =>
      patch<ProjectMembership>(`/dastarkhan/projects/${projectId}/members/${userId}`, perms),
    [patch]
  );

  const removeMember = useCallback(
    (projectId: string, userId: string) =>
      del(`/dastarkhan/projects/${projectId}/members/${userId}`),
    [del]
  );

  // ─── Graph ──────────────────────────────────────────────────────────────

  const getGraph = useCallback(
    (projectId: string, limit = 10000) =>
      get<GraphData>(`/dastarkhan/projects/${projectId}/graph?limit=${limit}`),
    [get]
  );

  const getGraphSchema = useCallback(
    (projectId: string) =>
      get<GraphSchema>(`/dastarkhan/projects/${projectId}/graph/schema`),
    [get]
  );

  // ─── Nodes ──────────────────────────────────────────────────────────────

  const listNodes = useCallback(
    (projectId: string, page = 1, perPage = 100, label?: string) => {
      let url = `/dastarkhan/projects/${projectId}/graph/nodes?page=${page}&per_page=${perPage}`;
      if (label) url += `&label=${encodeURIComponent(label)}`;
      return get<PaginatedResponse<GraphNode>>(url);
    },
    [get]
  );

  const createNode = useCallback(
    (projectId: string, data: { labels: string[]; properties?: Record<string, unknown> }) =>
      post<GraphNode>(`/dastarkhan/projects/${projectId}/graph/nodes`, data),
    [post]
  );

  const getNode = useCallback(
    (projectId: string, nodeId: string) =>
      get<GraphNode>(`/dastarkhan/projects/${projectId}/graph/nodes/${encodeURIComponent(nodeId)}`),
    [get]
  );

  const updateNode = useCallback(
    (projectId: string, nodeId: string, data: { labels?: string[]; properties?: Record<string, unknown> }) =>
      patch<GraphNode>(`/dastarkhan/projects/${projectId}/graph/nodes/${encodeURIComponent(nodeId)}`, data),
    [patch]
  );

  const deleteNode = useCallback(
    (projectId: string, nodeId: string) =>
      del(`/dastarkhan/projects/${projectId}/graph/nodes/${encodeURIComponent(nodeId)}`),
    [del]
  );

  // ─── Edges ──────────────────────────────────────────────────────────────

  const listEdges = useCallback(
    (projectId: string, page = 1, perPage = 100, type?: string) => {
      let url = `/dastarkhan/projects/${projectId}/graph/edges?page=${page}&per_page=${perPage}`;
      if (type) url += `&type=${encodeURIComponent(type)}`;
      return get<PaginatedResponse<GraphEdge>>(url);
    },
    [get]
  );

  const createEdge = useCallback(
    (projectId: string, data: { type: string; source_node_id: string; target_node_id: string; properties?: Record<string, unknown> }) =>
      post<GraphEdge>(`/dastarkhan/projects/${projectId}/graph/edges`, data),
    [post]
  );

  const getEdge = useCallback(
    (projectId: string, edgeId: string) =>
      get<GraphEdge>(`/dastarkhan/projects/${projectId}/graph/edges/${encodeURIComponent(edgeId)}`),
    [get]
  );

  const updateEdge = useCallback(
    (projectId: string, edgeId: string, data: { properties?: Record<string, unknown> }) =>
      patch<GraphEdge>(`/dastarkhan/projects/${projectId}/graph/edges/${encodeURIComponent(edgeId)}`, data),
    [patch]
  );

  const deleteEdge = useCallback(
    (projectId: string, edgeId: string) =>
      del(`/dastarkhan/projects/${projectId}/graph/edges/${encodeURIComponent(edgeId)}`),
    [del]
  );

  // ─── Queries ────────────────────────────────────────────────────────────

  const queryNeighbors = useCallback(
    (projectId: string, nodeId: string, depth = 1) =>
      post<GraphData>(`/dastarkhan/projects/${projectId}/graph/query/neighbors`, { node_id: nodeId, depth }),
    [post]
  );

  const queryShortestPath = useCallback(
    (projectId: string, sourceNodeId: string, targetNodeId: string) =>
      post<GraphData>(`/dastarkhan/projects/${projectId}/graph/query/shortest-path`, {
        source_node_id: sourceNodeId,
        target_node_id: targetNodeId,
      }),
    [post]
  );

  const querySubgraph = useCallback(
    (projectId: string, nodeLabels?: string[], relationshipTypes?: string[]) =>
      post<GraphData>(`/dastarkhan/projects/${projectId}/graph/query/subgraph`, {
        node_labels: nodeLabels,
        relationship_types: relationshipTypes,
      }),
    [post]
  );

  const querySearch = useCallback(
    (projectId: string, query: string) =>
      post<GraphData>(`/dastarkhan/projects/${projectId}/graph/query/search`, { query }),
    [post]
  );

  // ─── Chat ───────────────────────────────────────────────────────────────

  const chatQuery = useCallback(
    (projectId: string, message: string, sessionId: string, contextLevel = 'schema_only') =>
      post<ChatResponse>(`/dastarkhan/projects/${projectId}/chat`, {
        message,
        session_id: sessionId,
        context_level: contextLevel,
      }),
    [post]
  );

  // ─── Export ─────────────────────────────────────────────────────────────

  const exportGraph = useCallback(
    async (projectId: string, format: string, nodeIds?: string[]) => {
      // Export needs raw blob response, not JSON parsing
      // We use the auth token from context and do a manual fetch
      const body = JSON.stringify({ format, node_ids: nodeIds ?? null });
      // Post returns JSON by default, but export returns binary
      // The actual download is handled via the export component
      return post<unknown>(`/dastarkhan/projects/${projectId}/export`, { format, node_ids: nodeIds ?? null });
    },
    [post]
  );

  // ─── Project Admin ─────────────────────────────────────────────────────

  const deleteProject = useCallback(
    (projectId: string) =>
      del(`/dastarkhan/projects/${projectId}`),
    [del]
  );

  const clearProject = useCallback(
    (projectId: string) =>
      post<ClearResult>(`/dastarkhan/projects/${projectId}/clear`, {}),
    [post]
  );

  const getBackupStatus = useCallback(
    (projectId: string) =>
      get<BackupStatus>(`/dastarkhan/projects/${projectId}/backup-status`),
    [get]
  );

  // ─── Rollback ─────────────────────────────────────────────────────────

  const getLastUndoable = useCallback(
    (projectId: string) =>
      get<UndoableTransaction>(`/dastarkhan/projects/${projectId}/rollback/last-undoable`),
    [get]
  );

  const executeUndo = useCallback(
    (projectId: string, transactionId: number, force = false) =>
      post<UndoResponse | UndoConflict>(`/dastarkhan/projects/${projectId}/rollback/undo`, {
        transaction_id: transactionId,
        ...(force ? { force: true } : {}),
      }),
    [post]
  );

  const previewRollback = useCallback(
    (projectId: string, targetTransactionId: number) =>
      get<RollbackPreview>(`/dastarkhan/projects/${projectId}/rollback/preview?target_transaction_id=${targetTransactionId}`),
    [get]
  );

  const executePointInTime = useCallback(
    (projectId: string, targetTransactionId: number) =>
      post<RollbackResult>(`/dastarkhan/projects/${projectId}/rollback/point-in-time`, {
        target_transaction_id: targetTransactionId,
      }),
    [post]
  );

  const continueRollback = useCallback(
    (projectId: string, rollbackBatchId: string, action: 'skip' | 'force' | 'abort', conflictTransactionId: number) =>
      post<RollbackResult>(`/dastarkhan/projects/${projectId}/rollback/point-in-time/continue`, {
        rollback_batch_id: rollbackBatchId,
        action,
        conflict_transaction_id: conflictTransactionId,
      }),
    [post]
  );

  // ─── Audit Log ──────────────────────────────────────────────────────────

  const getAuditLog = useCallback(
    (projectId: string, params?: {
      page?: number;
      per_page?: number;
      operation?: string;
      entity_type?: string;
      since?: string;
      until?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', String(params.page));
      if (params?.per_page) searchParams.set('per_page', String(params.per_page));
      if (params?.operation) searchParams.set('operation', params.operation);
      if (params?.entity_type) searchParams.set('entity_type', params.entity_type);
      if (params?.since) searchParams.set('since', params.since);
      if (params?.until) searchParams.set('until', params.until);
      const qs = searchParams.toString();
      return get<PaginatedResponse<AuditLogEntry>>(
        `/dastarkhan/projects/${projectId}/audit-log${qs ? `?${qs}` : ''}`
      );
    },
    [get]
  );

  return {
    // Users
    getCurrentUser,
    listUsers,
    updateUserRole,
    // Projects
    listProjects,
    createProject,
    getProject,
    updateProject,
    updateCredentials,
    testCredentials,
    // Memberships
    listMembers,
    addMember,
    updatePermissions,
    removeMember,
    // Graph
    getGraph,
    getGraphSchema,
    // Nodes
    listNodes,
    createNode,
    getNode,
    updateNode,
    deleteNode,
    // Edges
    listEdges,
    createEdge,
    getEdge,
    updateEdge,
    deleteEdge,
    // Queries
    queryNeighbors,
    queryShortestPath,
    querySubgraph,
    querySearch,
    // Chat
    chatQuery,
    // Export
    exportGraph,
    // Audit
    getAuditLog,
    // Project Admin
    deleteProject,
    clearProject,
    getBackupStatus,
    // Rollback
    getLastUndoable,
    executeUndo,
    previewRollback,
    executePointInTime,
    continueRollback,
  };
}
